import {
  BookOpen,
  ClipboardCheck,
  FileText,
  FlaskConical,
  Headphones,
  Hammer,
  MessagesSquare,
  Presentation,
  ScrollText,
  Video,
  type LucideIcon,
} from "lucide-react";

/**
 * Presentation for the eleven item types the course builder writes.
 *
 * Keep this in step with constants/courseAnatomy.js on the server — that file
 * is the source of truth for which types exist; this one only decides how they
 * look. A type missing here falls back to a book icon rather than breaking.
 */
export const ITEM_ICON: Record<string, LucideIcon> = {
  reading: BookOpen,
  slides: Presentation,
  video: Video,
  audio: Headphones,
  key_terms: ScrollText,
  homework: FileText,
  assessment: ClipboardCheck,
  exam: ClipboardCheck,
  project: Hammer,
  lab: FlaskConical,
  discussion: MessagesSquare,
};

export const ITEM_LABEL: Record<string, string> = {
  reading: "Reading",
  slides: "Slides",
  video: "Video",
  audio: "Audio",
  key_terms: "Key terms",
  homework: "Homework",
  assessment: "Assessment",
  exam: "Exam",
  project: "Project",
  lab: "Lab",
  discussion: "Discussion",
};

/**
 * A stable colour per course, so a course looks the same everywhere it appears.
 *
 * Guessed from the title for now. When Brallan's taxonomy carries a subject on
 * the course, this should read that instead — the guess is a placeholder for
 * real data, not a design decision.
 */
const SUBJECT_ACCENTS: Array<[RegExp, string]> = [
  [/math|algebra|geometry|calculus|statistic/i, "#2E4A78"],
  [/bio|chem|physics|science|anatomy/i, "#6B5CA5"],
  [/english|language arts|ela|literature|writing|reading/i, "#0AA0C4"],
  [/history|government|civics|social/i, "#1B3560"],
];

const FALLBACK_ACCENTS = ["#087590", "#2E4A78", "#6B5CA5", "#0AA0C4", "#1B3560"];

/**
 * Calendar class colours. Spread around the wheel on purpose — hashing into a
 * tight teal/navy set made three enrolled classes look like one.
 */
const COURSE_CALENDAR_COLORS = [
  "#1B9E4B",
  "#D12B6A",
  "#2563EB",
  "#D97706",
  "#7C3AED",
  "#0F766E",
  "#DC2626",
  "#92400E",
];

export function lockedUntilCopy(title?: string | null) {
  const name = String(title || "").trim();
  return name
    ? `Locked until you finish ${name}`
    : "Locked until you finish the previous activity";
}

export function subjectAccent(title: string): string {
  for (const [pattern, colour] of SUBJECT_ACCENTS) {
    if (pattern.test(title)) return colour;
  }
  // Stable per title rather than random, so a course does not change colour
  // between the course card and the trail.
  let hash = 0;
  for (let i = 0; i < title.length; i += 1) hash = (hash * 31 + title.charCodeAt(i)) >>> 0;
  return FALLBACK_ACCENTS[hash % FALLBACK_ACCENTS.length];
}

export function courseAccent(courseId: string, title = "") {
  const key = String(courseId || title);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return COURSE_CALENDAR_COLORS[hash % COURSE_CALENDAR_COLORS.length];
}

/** First class green, second rose, third blue — no two enrolled courses share a neighbour hue. */
export function calendarAccents(courses: Array<{ id: string }>) {
  const map: Record<string, string> = {};
  courses.forEach((course, i) => {
    map[course.id] = COURSE_CALENDAR_COLORS[i % COURSE_CALENDAR_COLORS.length];
  });
  return map;
}
